<script>
// App-level password guard + change form.
//  - Voluntary: opened from the profile dropdown via the
//    `worksched:open-change-password` window event.
//  - Forced: when the current user has must_change_password (admin-provisioned
//    or admin-reset account) the modal is shown non-dismissible on login.
//  - Reminder: when the password is older than 90 days (password_expired) a
//    dismissible prompt offers "Change now" or "Keep current password"
//    (snoozed for the session).
export default {
  data() {
    return {
      show: false,
      forced: false,
      reminderShow: false,
      processing: false,
      submitError: "",
      fieldErrors: {},
      showPasswords: false,
      form: { current: "", next: "", confirm: "" },
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.currentUser;
    },
    // Re-evaluate the guard whenever identity or the flags change.
    guardKey() {
      const u = this.currentUser;
      return u ? `${u.id}|${u.must_change_password ? 1 : 0}|${u.password_expired ? 1 : 0}` : "";
    },
    anyOpen() {
      return this.show || this.reminderShow;
    },
  },
  watch: {
    guardKey() {
      this.evaluate();
    },
    anyOpen(v) {
      if (typeof document === "undefined") return;
      document.body.style.overflow = v ? "hidden" : "";
    },
  },
  mounted() {
    window.addEventListener("worksched:open-change-password", this.openVoluntary);
    this.evaluate();
  },
  beforeUnmount() {
    window.removeEventListener("worksched:open-change-password", this.openVoluntary);
    if (typeof document !== "undefined") document.body.style.overflow = "";
  },
  methods: {
    evaluate() {
      const u = this.currentUser;
      if (!u) {
        this.show = this.reminderShow = this.forced = false;
        return;
      }
      if (u.must_change_password) {
        this.forced = true;
        this.reminderShow = false;
        this.resetForm();
        this.show = true;
      } else if (u.password_expired && !this.isSnoozed()) {
        this.reminderShow = true;
      }
    },
    snoozeKey() {
      return `worksched.pwdSnooze.${this.currentUser?.id}`;
    },
    isSnoozed() {
      try {
        return sessionStorage.getItem(this.snoozeKey()) === "1";
      } catch {
        return false;
      }
    },
    resetForm() {
      this.form = { current: "", next: "", confirm: "" };
      this.submitError = "";
      this.fieldErrors = {};
      this.showPasswords = false;
    },
    openVoluntary() {
      this.forced = false;
      this.reminderShow = false;
      this.resetForm();
      this.show = true;
    },
    openFromReminder() {
      this.reminderShow = false;
      this.openVoluntary();
    },
    keepCurrent() {
      try {
        sessionStorage.setItem(this.snoozeKey(), "1");
      } catch {
        /* ignore */
      }
      this.reminderShow = false;
    },
    close() {
      if (this.forced || this.processing) return;
      this.show = false;
    },
    async logoutNow() {
      try {
        await this.$store.dispatch("auth/logOut");
      } finally {
        this.show = false;
        this.$router.push({ name: "login" });
      }
    },
    validate() {
      const errs = {};
      if (!this.form.current) errs.current_password = "Enter your current password.";
      if (!this.form.next) errs.new_password = "Enter a new password.";
      else if (this.form.next.length < 8) errs.new_password = "Must be at least 8 characters.";
      if (this.form.confirm !== this.form.next) errs.new_password_confirmation = "Passwords do not match.";
      this.fieldErrors = errs;
      return Object.keys(errs).length === 0;
    },
    async submit() {
      this.submitError = "";
      if (!this.validate()) return;
      this.processing = true;
      try {
        await this.$store.dispatch("auth/changePassword", {
          currentPassword: this.form.current,
          newPassword: this.form.next,
          newPasswordConfirmation: this.form.confirm,
        });
        try {
          sessionStorage.removeItem(this.snoozeKey());
        } catch {
          /* ignore */
        }
        this.forced = false;
        this.show = false;
        this.$swal?.fire({ icon: "success", title: "Password changed", timer: 1400, showConfirmButton: false });
      } catch (e) {
        const errs = e.response?.data?.errors;
        if (errs) this.fieldErrors = Object.fromEntries(Object.entries(errs).map(([k, v]) => [k, [].concat(v)[0]]));
        this.submitError = e.response?.data?.message || e.message || "Could not change password.";
      } finally {
        this.processing = false;
      }
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <!-- 90-day reminder -->
    <div v-if="reminderShow" class="ws-pw-backdrop" @click.self="keepCurrent">
      <div class="ws-pw-dialog ws-pw-dialog--sm">
        <div class="ws-pw-card">
          <header class="ws-pw-header">
            <h5 class="ws-pw-title"><i class="bx bx-shield-quarter me-1"></i> Time to update your password</h5>
          </header>
          <div class="ws-pw-body">
            <p class="mb-0">
              Your password is more than 90 days old. For your security we recommend changing it now.
              You can also keep your current password for now.
            </p>
          </div>
          <footer class="ws-pw-footer">
            <button type="button" class="btn btn-light" @click="keepCurrent">Keep current password</button>
            <button type="button" class="btn ws-pw-primary" @click="openFromReminder">Change now</button>
          </footer>
        </div>
      </div>
    </div>

    <!-- Change password form -->
    <div v-if="show" class="ws-pw-backdrop" @click.self="close">
      <div class="ws-pw-dialog">
        <div class="ws-pw-card">
          <header class="ws-pw-header">
            <h5 class="ws-pw-title">
              <i class="bx bx-lock-alt me-1"></i>
              {{ forced ? "Set a new password" : "Change Password" }}
            </h5>
            <button v-if="!forced" type="button" class="ws-pw-close" aria-label="Close" @click="close">
              <i class="bx bx-x"></i>
            </button>
          </header>

          <div class="ws-pw-body">
            <div v-if="forced" class="alert alert-warning py-2 mb-3">
              For security, you must set a new password before continuing.
            </div>
            <div v-if="submitError" class="alert alert-danger py-2 mb-3">{{ submitError }}</div>

            <label class="form-label small text-muted mb-1">Current password</label>
            <input
              v-model="form.current"
              :type="showPasswords ? 'text' : 'password'"
              class="form-control mb-1"
              :class="{ 'is-invalid': fieldErrors.current_password }"
              autocomplete="current-password"
            />
            <div v-if="fieldErrors.current_password" class="text-danger small mb-2">{{ fieldErrors.current_password }}</div>
            <div v-else class="mb-2"></div>

            <label class="form-label small text-muted mb-1">New password</label>
            <input
              v-model="form.next"
              :type="showPasswords ? 'text' : 'password'"
              class="form-control mb-1"
              :class="{ 'is-invalid': fieldErrors.new_password }"
              autocomplete="new-password"
            />
            <div v-if="fieldErrors.new_password" class="text-danger small mb-2">{{ fieldErrors.new_password }}</div>
            <div v-else class="form-text mb-2">At least 8 characters.</div>

            <label class="form-label small text-muted mb-1">Confirm new password</label>
            <input
              v-model="form.confirm"
              :type="showPasswords ? 'text' : 'password'"
              class="form-control mb-1"
              :class="{ 'is-invalid': fieldErrors.new_password_confirmation }"
              autocomplete="new-password"
              @keyup.enter="submit"
            />
            <div v-if="fieldErrors.new_password_confirmation" class="text-danger small mb-2">{{ fieldErrors.new_password_confirmation }}</div>

            <div class="form-check mt-2">
              <input id="ws-pw-show" v-model="showPasswords" type="checkbox" class="form-check-input" />
              <label class="form-check-label small" for="ws-pw-show">Show passwords</label>
            </div>
          </div>

          <footer class="ws-pw-footer">
            <button v-if="forced" type="button" class="btn btn-link text-muted me-auto" @click="logoutNow">Sign out</button>
            <button v-else type="button" class="btn btn-light" :disabled="processing" @click="close">Cancel</button>
            <button type="button" class="btn ws-pw-primary" :disabled="processing" @click="submit">
              <span v-if="processing"><span class="spinner-border spinner-border-sm me-1"></span> Saving…</span>
              <span v-else>{{ forced ? "Set password" : "Change password" }}</span>
            </button>
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ws-pw-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1070;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.ws-pw-dialog {
  width: 100%;
  max-width: 460px;
}
.ws-pw-dialog--sm {
  max-width: 420px;
}
.ws-pw-card {
  background: #fff;
  border-radius: 0.6rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}
.ws-pw-header {
  background: #F57C00;
  color: #fff;
  padding: 0.85rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ws-pw-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #fff;
}
.ws-pw-close {
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
}
.ws-pw-body {
  padding: 1.25rem;
  overflow-y: auto;
}
.ws-pw-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid #e9ecef;
}
.ws-pw-primary {
  background: #F57C00;
  border: 1px solid #F57C00;
  color: #fff;
}
.ws-pw-primary:hover,
.ws-pw-primary:focus {
  background: #EA580C;
  border-color: #EA580C;
  color: #fff;
}
</style>
